import { ArrowRight, Cloud, LockKeyhole, RotateCcw, Sparkles } from "lucide-react";
import { FormEvent, useEffect, useMemo, useState } from "react";
import type { DecisionReport, Locale } from "../shared/types";
import { analyzeDecision, askQuestions, getShare } from "./api";
import { clearDraft, loadDraft, saveDraft, type LocalDraft } from "./storage";
import { ReportView } from "./ReportView";

const examples = ["我要不要接受新的工作機會？", "這個副業值得繼續投入嗎？", "我該搬去另一個城市嗎？"];

function Brand({ compact = false }: { compact?: boolean }) {
  return <a href="/" className={`brand ${compact ? "brand--compact" : ""}`} aria-label="定見首頁"><span>定</span><strong>定見</strong><small>想清楚，再決定</small></a>;
}

function SharedPage({ id }: { id: string }) {
  const [report, setReport] = useState<DecisionReport | null>(null);
  const [error, setError] = useState("");
  useEffect(() => { void getShare(id).then((snapshot) => setReport(snapshot.report)).catch((reason) => setError(reason instanceof Error ? reason.message : "無法載入分享")); }, [id]);
  return <><TopBar />{report ? <ReportView report={report} shared /> : <div className="center-state">{error || "正在打開這份決策…"}</div>}</>;
}

function TopBar({ locale, onLocale }: { locale?: Locale; onLocale?: (locale: Locale) => void }) {
  return <header className="topbar"><Brand compact /><div className="topbar__right"><span><LockKeyhole size={14} />免登入 · 草稿留在本機</span>{locale && onLocale && <button className="locale-switch" onClick={() => onLocale(locale === "zh-TW" ? "zh-CN" : "zh-TW")}>{locale === "zh-TW" ? "简" : "繁"}</button>}</div></header>;
}

export function App() {
  const shareId = useMemo(() => window.location.pathname.match(/^\/share\/([a-f0-9]{16})$/)?.[1], []);
  if (shareId) return <SharedPage id={shareId} />;

  const initial = loadDraft();
  const [step, setStep] = useState<LocalDraft["step"]>(initial?.step ?? "start");
  const [decision, setDecision] = useState(initial?.decision ?? "");
  const [questions, setQuestions] = useState(initial?.questions ?? []);
  const [answers, setAnswers] = useState(initial?.answers ?? []);
  const [coachIntro, setCoachIntro] = useState(initial?.coachIntro ?? "");
  const [report, setReport] = useState<DecisionReport | null>(initial?.report ?? null);
  const [locale, setLocale] = useState<Locale>(initial?.locale ?? "zh-TW");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (decision || step !== "start") saveDraft({ step, decision, questions, answers, locale, coachIntro, report: report ?? undefined });
  }, [step, decision, questions, answers, locale, coachIntro, report]);

  const start = async (event: FormEvent) => {
    event.preventDefault();
    setError("");
    if (decision.trim().length < 5) { setError("請用一句完整的話，說說你正在猶豫什麼。"); return; }
    setLoading(true);
    try {
      const result = await askQuestions({ decision: decision.trim(), locale });
      setQuestions(result.questions);
      setAnswers(result.questions.map(() => ""));
      setCoachIntro(result.coachIntro);
      setStep("clarify");
    } catch (reason) { setError(reason instanceof Error ? reason.message : "暫時無法開始"); }
    finally { setLoading(false); }
  };

  const analyze = async (event: FormEvent) => {
    event.preventDefault();
    if (answers.some((answer) => answer.trim().length < 2)) { setError("每個問題都值得你給一句真實的回答。"); return; }
    setLoading(true); setError("");
    try {
      const result = await analyzeDecision({ decision, locale, answers: questions.map((question, index) => ({ question, answer: answers[index] })) });
      setReport(result); setStep("report"); window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (reason) { setError(reason instanceof Error ? reason.message : "暫時無法完成分析"); }
    finally { setLoading(false); }
  };

  const restart = () => {
    clearDraft(); setStep("start"); setDecision(""); setQuestions([]); setAnswers([]); setReport(null); setError(""); window.scrollTo({ top: 0, behavior: "smooth" });
  };

  if (step === "report" && report) return <><TopBar locale={locale} onLocale={setLocale} /><ReportView report={report} onChange={setReport} onRestart={restart} privateAnswers={answers} /></>;

  return <div className="app-shell">
    <TopBar locale={locale} onLocale={setLocale} />
    {step === "start" ? <main className="start-page">
      <section className="hero">
        <div className="hero__copy">
          <span className="eyebrow"><Sparkles size={14} />一位溫和，但敢指出盲點的教練</span>
          <h1>把腦中的<br /><em>一團亂</em>，<br />攤開來看。</h1>
          <p>定見不替你做決定。它幫你找出真正重要的事、沒看見的風險，以及今天就能走的下一步。</p>
        </div>
        <form className="decision-entry" onSubmit={start}>
          <div className="entry-head"><span>現在，先說一件事</span><small>約 3–5 分鐘</small></div>
          <label htmlFor="decision">你正在猶豫什麼？</label>
          <textarea id="decision" value={decision} onChange={(event) => setDecision(event.target.value)} placeholder="例如：我該留在現在的公司，還是接受新的 offer？" maxLength={600} rows={5} />
          <div className="example-row">{examples.map((example) => <button type="button" key={example} onClick={() => setDecision(example)}>{example}</button>)}</div>
          {error && <p className="error-text">{error}</p>}
          <button className="button button--primary" type="submit" disabled={loading}>{loading ? "正在整理問題…" : <>開始想清楚<ArrowRight size={18} /></>}</button>
          <p className="privacy-line"><LockKeyhole size={14} />你的輸入先保存在這台裝置，除非你主動生成分享連結。</p>
        </form>
      </section>
      <section className="process-strip" aria-label="決策流程"><span>一個困惑</span><i /><span>四個追問</span><i /><span>一張決策圖</span><i /><strong>一個下一步</strong></section>
      <section className="promise-grid"><article><span>01</span><h2>不是算命</h2><p>不把模糊建議包裝成確定答案，而是標出信心和待確認事項。</p></article><article><span>02</span><h2>不是聊天紀錄</h2><p>輸出一份可調權重、能回頭檢查假設的結構化決策。</p></article><article><span>03</span><h2>不是替你選</h2><p>結論可以明確，但選擇仍屬於你；你看得見它從何而來。</p></article></section>
    </main> : <main className="clarify-page">
      <div className="clarify-aside"><button className="text-button" onClick={() => setStep("start")}><RotateCcw size={15} />修改問題</button><span className="eyebrow">釐清 · 4 個問題</span><h1>{decision}</h1><div className="aside-note"><Cloud size={19} /><p>回答沒有標準答案。寫下第一個真實反應，比寫得完整更重要。</p></div></div>
      <form className="clarify-form" onSubmit={analyze}>
        <header><span className="coach-mark">定</span><p>{coachIntro}</p></header>
        {questions.map((question, index) => <label className="question-card" key={question}><span>{String(index + 1).padStart(2, "0")}</span><strong>{question}</strong><textarea aria-label={question} value={answers[index] ?? ""} onChange={(event) => setAnswers((current) => current.map((answer, answerIndex) => answerIndex === index ? event.target.value : answer))} placeholder="寫下你的真實想法…" rows={3} maxLength={1000} /></label>)}
        {error && <p className="error-text">{error}</p>}
        <button className="button button--primary" type="submit" disabled={loading}>{loading ? "正在梳理你的決定…" : <>生成我的決策圖<ArrowRight size={18} /></>}</button>
      </form>
    </main>}
    <footer><Brand compact /><p>重要的醫療、法律或財務決定，請同時諮詢合資格的專業人士。</p></footer>
  </div>;
}
